import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import { Add as AddIcon, Delete as DeleteIcon, Group as GroupIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddRoleModal from './AddRoleModal';
import { ROLE_DEFAULT } from '../../../constants';
import { hasPermission } from '../../../utils/permissions';
import {
    fetchInitPermissions,
    fetchRoles,
    fetchRoleById,
    addRole,
    updateRole,
    selectRoles,
    selectRolesLoading,
    selectRolesError,
    selectRolesSuccessMessage,
    selectRolesTotal,
    selectRolesPage,
    selectRolesPageSize,
    selectRolesSortColumn,
    selectRolesSortDirection,
    selectRolesFilterName,
    selectSelectedRole,
    selectDefaultPermissions,
    selectModules,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    deleteRole,
    bulkDeleteRoles,
    clearError,
    clearSuccessMessage,
    clearSelectedRole,
} from './slice';
import './Roles.css';

function Roles() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const roles = useSelector(selectRoles);
    const loading = useSelector(selectRolesLoading);
    const error = useSelector(selectRolesError);
    const successMessage = useSelector(selectRolesSuccessMessage);
    const total = useSelector(selectRolesTotal);
    const page = useSelector(selectRolesPage);
    const pageSize = useSelector(selectRolesPageSize);
    const sortColumn = useSelector(selectRolesSortColumn);
    const sortDirection = useSelector(selectRolesSortDirection);
    const filterName = useSelector(selectRolesFilterName);
    const selectedRole = useSelector(selectSelectedRole);
    const defaultPermissions = useSelector(selectDefaultPermissions);
    const modules = useSelector(selectModules);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // Check permissions
    const canAddRole = hasPermission('role', 'addRole');
    const canEditRole = hasPermission('role', 'editRole');
    const canDeleteRole = hasPermission('role', 'deleteRole');

    // Fetch init permissions on mount
    useEffect(() => {
        dispatch(fetchInitPermissions());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchRoles({ page, pageSize, sortColumn, sortDirection, filterName }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName]);

    // Show error notification
    useEffect(() => {
        if (error) {
            console.log('[Roles index] Error value:', error);
            console.log('[Roles index] Error type:', typeof error);
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch roles to update the list
            dispatch(fetchRoles({ page, pageSize, sortColumn, sortDirection, filterName }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

    // Define table columns
    const columns = [
        {
            key: 'name',
            label: t('roles.table.name'),
        },
        {
            key: 'description',
            label: t('roles.table.description'),
        },
        {
            key: 'isActive',
            label: t('roles.table.status'),
            render: (value) => {
                // value is the isActive field itself (true/false/null)
                const isActive = value === true || value === 1 || value === '1';
                const hasStatus = value !== undefined && value !== null;

                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: hasStatus ? (isActive ? '#d4edda' : '#f8d7da') : '#e2e8f0',
                            color: hasStatus ? (isActive ? '#155724' : '#721c24') : '#64748b',
                        }}
                    >
                        {hasStatus ? (isActive ? t('roles.status.active') : t('roles.status.inactive')) : '-'}
                    </Box>
                );
            },
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch role details by ID
        dispatch(fetchRoleById(row.id));
        setShowEditDialog(true);
    };

    // Check if role is deletable (not a default role)
    const isRoleDeletable = (row) => {
        return row.type !== ROLE_DEFAULT;
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setRoleToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (roleToDelete?.id) {
            dispatch(deleteRole(roleToDelete.id));
        }
        setShowDeleteDialog(false);
        setRoleToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setRoleToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteRoles(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle filter change
    const handleFilterChange = (event) => {
        dispatch(setFilterName(event.target.value));
    };

    // Handle add role - show dialog
    const handleAddRole = () => {
        dispatch(clearSelectedRole());
        setShowAddDialog(true);
    };

    // Handle close add role dialog
    const handleCloseAddRole = () => {
        setShowAddDialog(false);
        dispatch(clearSelectedRole());
    };

    // Confirm add role
    const handleConfirmAddRole = (roleData) => {
        dispatch(addRole(roleData));
        setShowAddDialog(false);
    };

    // Handle close edit role dialog
    const handleCloseEditRole = () => {
        setShowEditDialog(false);
        dispatch(clearSelectedRole());
    };

    // Confirm edit role
    const handleConfirmEditRole = (roleData) => {
        dispatch(updateRole(roleData));
        setShowEditDialog(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <GroupIcon fontSize="small" />
                        <Typography variant="body2">{t('roles.breadcrumb.users')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('roles.breadcrumb.roles')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('roles.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddRole && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddRole}
                                id="addRole"
                                name="addRole"
                                data-testid="roles-button-addRole"
                            >
                                {t('roles.addRole')}
                            </Button>
                        )}
                        {canDeleteRole && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0}
                                id="deleteSelected"
                                name="deleteSelected"
                                data-testid="roles-button-deleteSelected"
                            >
                                {t('roles.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <Input
                                label={t('roles.filterByName')}
                                value={filterName}
                                onChange={handleFilterChange}
                                placeholder={t('roles.filterByNamePlaceholder')}
                                id="filterName"
                                name="filterName"
                                data-testid="roles-input-filterName"
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="roles-table"
                        columns={columns}
                        data={roles}
                        selectable={true}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        isRowSelectable={(row) => isRoleDeletable(row)}
                        onEdit={canEditRole ? handleEdit : null}
                        onDelete={canDeleteRole ? handleDelete : null}
                        isRowDeletable={(row) => isRoleDeletable(row)}
                        loading={loading}
                        emptyMessage={t('roles.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={['name', 'description', 'isActive']}
                    />
                </Box>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('roles.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('roles.deleteDialog.message', {
                            name: roleToDelete?.name || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="roles-button-cancelDelete"
                    >
                        {t('roles.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="roles-button-confirmDelete"
                    >
                        {t('roles.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('roles.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('roles.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="roles-button-cancelBulkDelete"
                    >
                        {t('roles.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="roles-button-confirmBulkDelete"
                    >
                        {t('roles.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Role Modal */}
            <AddRoleModal
                open={showAddDialog}
                onClose={handleCloseAddRole}
                onConfirm={handleConfirmAddRole}
                defaultPermissions={defaultPermissions}
                modules={modules}
            />

            {/* Edit Role Modal */}
            <AddRoleModal
                open={showEditDialog}
                onClose={handleCloseEditRole}
                onConfirm={handleConfirmEditRole}
                roleData={selectedRole}
                defaultPermissions={defaultPermissions}
                modules={modules}
                editMode={true}
            />
        </Box>
    );
}

export default Roles;
